import { cn } from './cn.js';
import { quotaState } from './quota-level.js';

/**
 * Barre de quota.
 *
 * La couleur seule ne porte jamais l'information : le pourcentage est aussi
 * écrit, et la barre expose les attributs ARIA d'une progressbar. WCAG 1.4.1.
 */
export interface QuotaBarProps {
  used: number;
  limit: number;
  className?: string;
}

export function QuotaBar({ used, limit, className }: QuotaBarProps) {
  const { percent, level } = quotaState(used, limit);

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-baseline justify-between">
        <p className="text-sm text-text-muted">
          <span className="font-mono text-text">{used.toLocaleString('fr-FR')}</span>
          {' / '}
          {limit.toLocaleString('fr-FR')} images
        </p>
        <span
          className={cn(
            'rounded-full px-3 py-1 text-xs font-medium',
            level === 'ok' && 'bg-accent-subtle text-accent-text',
            level === 'warning' && 'bg-warning/10 text-warning',
            level === 'danger' && 'bg-danger/10 text-danger',
          )}
        >
          {percent} % utilisé
        </span>
      </div>

      {/* biome-ignore lint/a11y/useFocusableInteractive: `progressbar` est un
          rôle de restitution, pas de saisie — l'utilisateur n'agit pas dessus.
          Le rendre focusable ajouterait un arrêt de tabulation stérile. La
          règle range à tort ce rôle parmi les rôles interactifs. */}
      <div
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Consommation mensuelle"
        className="h-2 w-full overflow-hidden rounded-full bg-surface-raised"
      >
        <div
          className={cn(
            'h-full transition-[width] duration-[400ms] ease-out motion-reduce:transition-none',
            level === 'ok' && 'bg-accent',
            level === 'warning' && 'bg-warning',
            level === 'danger' && 'bg-danger',
          )}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
